/**
 * Delete Routes Hook
 * 
 * Custom hook for handling route delete operations with confirmation, batch processing,
 * progress tracking, and error handling.
 * 
 * @module useDeleteRoutes
 */

import { useState, useCallback } from 'react';
import { deleteRoute, type Environment, type UATRoute } from '../services/fixedRouteUatClient';
import { validateForDelete, type DeleteValidationData } from '../services/routesTransferValidation';
import { handleApiError } from '../utils/errorHandling';
import type { RouteOperationResult } from '../components/map/components/routes-transfer/OperationResults';

/**
 * Delete progress for a single route
 */
export interface RouteDeleteProgress {
  routeId: string;
  routeName: string;
  status: 'pending' | 'deleting' | 'success' | 'error';
  message: string;
  progress?: number; // 0-100
}

/**
 * Delete state
 */
export interface DeleteState {
  /** Whether delete is in progress */
  isDeleting: boolean;
  /** Whether validation is in progress */
  isValidating: boolean;
  /** Delete progress for each route */
  progress: Map<string, RouteDeleteProgress>;
  /** Delete results */
  results: RouteOperationResult[];
  /** Validation results */
  validation: DeleteValidationData | null;
  /** Current error */
  error: string | null;
}

/**
 * Hook return type
 */
export interface UseDeleteRoutesReturn {
  /** Delete state */
  deleteState: DeleteState;
  /** Start delete operation */
  startDelete: (
    selectedRoutes: UATRoute[],
    prerequisites: {
      jwtToken: string;
      productId: string;
      networkId: number | null;
      deleteUnconnectedStops: boolean;
      environment: Environment;
    }
  ) => Promise<void>;
  /** Validate routes before delete */
  validateRoutes: (
    selectedRoutes: UATRoute[],
    prerequisites: {
      jwtToken: string;
      productId: string;
      networkId: number | null;
      serviceDate?: string;
      timezone?: string;
      downloadAcknowledged?: boolean;
    }
  ) => Promise<DeleteValidationData | null>;
  /** Reset delete state */
  resetDelete: () => void;
}

/**
 * useDeleteRoutes Hook
 * 
 * Manages route delete operations with validation, batch processing, and progress tracking.
 */
export function useDeleteRoutes(): UseDeleteRoutesReturn {
  const [deleteState, setDeleteState] = useState<DeleteState>({
    isDeleting: false,
    isValidating: false,
    progress: new Map(),
    results: [],
    validation: null,
    error: null,
  });

  /**
   * Validate routes before delete
   */
  const validateRoutes = useCallback(
    async (
      selectedRoutes: UATRoute[],
      prerequisites: {
        jwtToken: string;
        productId: string;
        networkId: number | null;
        serviceDate?: string;
        timezone?: string;
        downloadAcknowledged?: boolean;
      }
    ): Promise<DeleteValidationData | null> => {
      if (selectedRoutes.length === 0) {
        return null;
      }

      setDeleteState((prev) => ({ ...prev, isValidating: true, error: null }));

      try {
        // Convert to selected route IDs
        const selectedRouteIds = new Set(selectedRoutes.map((r) => r.id));

        // Validate
        const validation = await validateForDelete(selectedRoutes, {
          jwtToken: prerequisites.jwtToken,
          productId: prerequisites.productId,
          networkId: prerequisites.networkId,
          serviceDate: prerequisites.serviceDate,
          timezone: prerequisites.timezone,
          downloadAcknowledged: prerequisites.downloadAcknowledged,
          selectedRouteIds,
        });

        setDeleteState((prev) => ({
          ...prev,
          validation,
          isValidating: false,
        }));

        return validation;
      } catch (error) {
        const friendlyError = handleApiError(error, 'Route validation');
        setDeleteState((prev) => ({
          ...prev,
          isValidating: false,
          error: friendlyError.message,
        }));
        return null;
      }
    },
    []
  );

  /**
   * Start delete operation
   */
  const startDelete = useCallback(
    async (
      selectedRoutes: UATRoute[],
      prerequisites: {
        jwtToken: string;
        productId: string;
        networkId: number | null;
        deleteUnconnectedStops: boolean;
        environment: Environment;
      }
    ) => {
      if (selectedRoutes.length === 0) {
        return;
      }

      // Initialize progress
      const progressMap = new Map<string, RouteDeleteProgress>();
      selectedRoutes.forEach((route) => {
        progressMap.set(route.id, {
          routeId: route.id,
          routeName: route.label || route.id,
          status: 'pending',
          message: 'Waiting...',
          progress: 0,
        });
      });

      setDeleteState({
        isDeleting: true,
        isValidating: false,
        progress: progressMap,
        results: [],
        validation: null,
        error: null,
      });

      const results: RouteOperationResult[] = [];

      // Delete routes sequentially
      for (let i = 0; i < selectedRoutes.length; i++) {
        const route = selectedRoutes[i];
        const progress = progressMap.get(route.id)!;

        try {
          // Update progress: deleting
          progress.status = 'deleting';
          progress.message = 'Deleting...';
          progress.progress = ((i + 0.5) / selectedRoutes.length) * 100;
          setDeleteState((prev) => ({
            ...prev,
            progress: new Map(progressMap),
          }));

          // Call API
          const response = await deleteRoute(
            prerequisites.jwtToken,
            prerequisites.productId,
            route.id,
            prerequisites.deleteUnconnectedStops,
            prerequisites.environment
          );

          // Check if deletion was successful
          if (response.success) {
            // Update progress: success
            progress.status = 'success';
            progress.message = 'Deleted successfully';
            progress.progress = ((i + 1) / selectedRoutes.length) * 100;
            setDeleteState((prev) => ({
              ...prev,
              progress: new Map(progressMap),
            }));

            results.push({
              routeId: route.id,
              routeName: route.label || route.id,
              success: true,
            });
          } else {
            // Update progress: error
            progress.status = 'error';
            progress.message = response.error || 'Failed to delete';
            progress.progress = ((i + 1) / selectedRoutes.length) * 100;
            setDeleteState((prev) => ({
              ...prev,
              progress: new Map(progressMap),
            }));

            results.push({
              routeId: route.id,
              routeName: route.label || route.id,
              success: false,
              error: response.error || 'Failed to delete route',
            });
          }
        } catch (error) {
          const friendlyError = handleApiError(error, `Delete route "${route.label || route.id}"`);

          // Update progress: error
          progress.status = 'error';
          progress.message = friendlyError.message;
          progress.progress = ((i + 1) / selectedRoutes.length) * 100;
          setDeleteState((prev) => ({
            ...prev,
            progress: new Map(progressMap),
          }));

          results.push({
            routeId: route.id,
            routeName: route.label || route.id,
            success: false,
            error: friendlyError.message,
          });
        }
      }

      // Finalize
      setDeleteState((prev) => ({
        ...prev,
        isDeleting: false,
        results,
      }));
    },
    []
  );

  /**
   * Reset delete state
   */
  const resetDelete = useCallback(() => {
    setDeleteState({
      isDeleting: false,
      isValidating: false,
      progress: new Map(),
      results: [],
      validation: null,
      error: null,
    });
  }, []);

  return {
    deleteState,
    startDelete,
    validateRoutes,
    resetDelete,
  };
}

